'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { usePathname } from 'next/navigation';
import markdown from '@wcj/markdown-to-html';

import { Container } from '@/atoms';
import { FlyOut, Prose } from '@/molecules';
import { Footer, Header } from '@/organisms';
import { Prefooter } from '@/templates';

// import LogoFooter from '@/assets/images/footer/Logo.png';
import CardGeneric from '@/assets/images/generic.jpg';
import LogoWhite from '@/assets/images/logo.png';
import LogoBlack from '@/assets/images/logodark.png';
import { routes } from '@/lib';
import {
  eyebrowClass,
  getImageWithFallback,
  getPageName,
  getRealUrl,
  getUserID,
  gtmPush,
  textStyleClass,
  toArray,
} from '@/utils';

export const Layout = ({
  children,
  theme = 'light',
  menu,
  preFooter,
  hasCustomFooter,
  footerStyle,
  transparentMenu = false,
  stickyMenu = false,
}: any) => {
  const logo = transparentMenu || theme === 'dark' ? LogoWhite : LogoBlack;
  // Cuando el header es transparente, al hacer scroll cambia a sólido y
  // necesitamos el logo oscuro para que se vea sobre el bg claro.
  const logoScrolled = transparentMenu ? LogoBlack : undefined;
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    gtmPush({
      event: 'init',
      user: {
        userId: getUserID(),
        loggedIn: '0',
        visitor_type: 'external',
      },
      page: {
        page_type: getPageName(),
      },
    });

    router?.events.on('routeChangeComplete', () => {
      gtmPush({
        event: 'new_page',
        page: {
          page_type: getPageName(),
        },
      });
    });
  }, [router?.events]);
  const topBarItems = menu?.find((itm: any) => itm.slug === 'topMenu')?.items;

  const topBar = topBarItems?.map((val: any, indice: number) => ({
    id: indice,
    title: val.title,
    url: val.url,
  }));

  const mainMenuEntry = menu?.find((itm: any) => itm.slug === 'mainMenu');
  const mainMenuData = mainMenuEntry?.items;
  const menuLayout = mainMenuEntry?.menuLayout || 'classic';
  const mainMenu: any[] = [];

  mainMenuData?.forEach((itm: any) => {
    const parent = itm.parent;
    const submenu = toArray(
      mainMenuData?.filter(
        (item: any) => item.parent?.documentId === itm.documentId,
      ),
    )
      ?.filter(Boolean)
      ?.map((item: any) => ({
        href: item.url,
        title: item.title,
      }));

    if (!parent) {
      mainMenu.push({
        href: itm.url,
        label: itm.title,
        children:
          submenu.length === 0 ? null : (
            <FlyOut menu={submenu} className="z-10 bg-transparent" />
          ),
      });
    }
  });

  return (
    <div className="flex min-h-screen flex-col">
      <Header
        logo={{
          alt: `Logo ${process.env.NEXT_PUBLIC_SITE_NAME}`,
          height: logo.height,
          src: logo.src,
          width: logo.width,
        }}
        logoScrolled={
          logoScrolled
            ? {
                alt: `Logo ${process.env.NEXT_PUBLIC_SITE_NAME}`,
                height: logoScrolled.height,
                src: logoScrolled.src,
                width: logoScrolled.width,
              }
            : undefined
        }
        menu={mainMenu}
        theme={theme}
        topBar={topBar}
        menuLayout={menuLayout}
        transparent={transparentMenu}
        sticky={stickyMenu}
      />

      <main className="grow">{children}</main>

      {((pathname !== routes.index &&
        pathname !== routes.contact &&
        pathname !== routes.faqs &&
        pathname !== routes.privacy &&
        pathname !== routes.legal) ||
        hasCustomFooter ||
        (pathname === routes.index && preFooter?.showOnHomepage) ||
        (pathname === routes.faqs && preFooter?.showOnFaqs)) && (
        <div
          id="faqs-contact"
          className="pt-(--prefooter-pt-mobile) lg:pt-(--prefooter-pt)"
        >
          <Prefooter
            footerStyle={footerStyle}
            composedLayout={!!preFooter?.composedLayout}
            contactOnly={pathname === routes.faqs}
            eyebrow={preFooter?.Eyebrow}
            title={preFooter?.Titulo}
            subtitle={preFooter?.Subtitulo}
            description={preFooter?.Descripcion}
            eyebrowStyleClass={eyebrowClass(preFooter?.EyebrowEstilo)}
            titleStyleClass={textStyleClass(preFooter?.TituloEstilo)}
            subtitleStyleClass={textStyleClass(preFooter?.SubtituloEstilo)}
            descriptionStyleClass={textStyleClass(preFooter?.DescripcionEstilo)}
            derechaTitleStyleClass={textStyleClass(
              preFooter?.TituloDerechaEstilo,
            )}
            derechaDescriptionStyleClass={textStyleClass(
              preFooter?.DescripcionDerechaEstilo,
            )}
            accordionTitleStyleClass={textStyleClass(
              preFooter?.AcordeonTituloEstilo,
            )}
            accordionDescriptionStyleClass={textStyleClass(
              preFooter?.AcordeonRespuestaEstilo,
            )}
            linkStyleClass={textStyleClass(preFooter?.EnlaceEstilo)}
            image={
              preFooter?.ImagenDerecha?.url
                ? {
                    src: getRealUrl(preFooter.ImagenDerecha.url),
                    alt: preFooter.ImagenDerecha.alternativeText ?? '',
                  }
                : undefined
            }
            imageShapeUrl={
              preFooter?.FormaImagenFaq?.Svg?.url
                ? getRealUrl(preFooter.FormaImagenFaq.Svg.url)
                : undefined
            }
            accordions={preFooter?.pregunta_frecuentes?.map((itm: any) => ({
              title: itm.Pregunta,
              // `Respuesta` es richtext (markdown) → HTML con estilos `Prose`.
              // `!` en los li: el CSS module de Prose los fuerza a body-m y
              // dentro del FAQ deben heredar el tamaño de la respuesta.
              description: itm.Respuesta ? (
                <Prose
                  html={markdown(itm.Respuesta).toString()}
                  className="[&_li]:text-[length:inherit]! [&_li]:leading-[inherit]!"
                />
              ) : null,
            }))}
            link={{
              label: 'Ver todas',
              href: routes.faqs,
            }}
            card={{
              title: preFooter?.TituloDerecha,
              image: getImageWithFallback(
                {
                  src: getRealUrl(preFooter?.ImagenDerecha?.url),
                  alt: preFooter?.ImagenDerecha?.alternativeText,
                },
                { src: CardGeneric.src, alt: 'Fallback image' },
              ),
              description: preFooter?.DescripcionDerecha,
              link: {
                label: preFooter?.TextoBotonDerecha,
                href: preFooter?.LinkBotonDerecha,
              },
            }}
            cta={{
              title: preFooter?.TituloDerecha,
              subtitle: preFooter?.DescripcionDerecha,
              backgroundImage: preFooter?.ImagenContacto?.url
                ? {
                    src: getRealUrl(preFooter.ImagenContacto.url),
                    alt: preFooter.ImagenContacto.alternativeText ?? '',
                  }
                : undefined,
              button:
                preFooter?.TextoBotonDerecha && preFooter?.LinkBotonDerecha
                  ? {
                      label: preFooter.TextoBotonDerecha,
                      href: preFooter.LinkBotonDerecha,
                    }
                  : undefined,
            }}
          />
        </div>
      )}

      <Footer
        footerStyle={footerStyle}
        image={{
          alt: `Logo ${process.env.NEXT_PUBLIC_SITE_NAME}`,
          height: LogoWhite.height,
          src: LogoWhite.src,
          width: LogoWhite.width,
        }}
        disclaimer="Las infografías mostradas en la Web, referentes a instalaciones de zonas comunes y piscina, están sujetas a ejecución del proyecto, no constituyendo carácter vinculante, ni contractual. Los elementos, tanto de ajardinamiento como decorativos, que figuran son orientativos. Toda la información relativa al Real Decrerto 515/89, de 21 de abril se encuentra en nuestras oficinas. Las imágenes, referentes a cocinas y demás dependencias de las viviendas publicitadas, referentes a mobiliarios, enseres y plantas mostrados, son ilustrativas y decorativas, no constituyendo carácter vinculante, ni contractual."
        className={
          router?.asPath.includes(routes.flats.flat('')) ? 'mb-16 md:mb-0' : ''
        }
      >
        <Footer.Column
          menu={menu
            ?.find((itm: any) => itm.slug === 'footer-menu-column-1')
            ?.items?.map((itm: any) => ({
              href: itm.url,
              label: itm.title,
            }))}
          title={
            menu?.find((itm: any) => itm.slug === 'footer-menu-column-1')
              ?.title || 'Sobre Nosotros'
          }
        />
        <Footer.Column
          menu={menu
            ?.find((itm: any) => itm.slug === 'footer-menu-column-2')
            ?.items?.map((itm: any) => ({
              href: itm.url,
              label: itm.title,
            }))}
          title={
            menu?.find((itm: any) => itm.slug === 'footer-menu-column-2')
              ?.title || 'Nuestras Viviendas'
          }
        />
        {footerStyle !== 'boxed' && (
          <Footer.Column
            menu={menu
              ?.find((itm: any) => itm.slug === 'footer-menu-column-3')
              ?.items?.map((itm: any) => ({
                href: itm.url,
                label: itm.title,
              }))}
            title={
              menu?.find((itm: any) => itm.slug === 'footer-menu-column-3')
                ?.title || 'Nuestros servicios'
            }
          />
        )}
        <Footer.Column
          title={{
            href: routes.contact,
            label:
              menu?.find((itm: any) => itm.slug === 'footer-menu-column-4')
                ?.title || 'Contáctanos',
          }}
          menu={menu
            ?.find((itm: any) => itm.slug === 'footer-menu-column-4')
            ?.items?.map((itm: any) => ({
              href: itm.url,
              label: itm.title,
            }))}
        />
        <Footer.Legal
          className={`${footerStyle !== 'boxed' ? 'text-neutrals-0' : ''}`}
          legales={[
            '© 2026 | All rights reserved',
            ...menu
              ?.find((itm: any) => itm.slug === 'legal')
              ?.items?.map((itm: any) => ({
                href: itm.url,
                label: itm.title,
              })),
          ]}
        />
      </Footer>
    </div>
  );
};

Layout.displayName = 'Layout';
